/**
 * Global UpgradeModal — fires when a 403 limit-hit / Free-tier-blocks-video
 * error comes back from the backend.
 *
 * Listens for the `lumora:upgrade-required` custom event so any axios
 * interceptor or component can trigger it without prop drilling.
 *
 *   window.dispatchEvent(new CustomEvent('lumora:upgrade-required', {
 *     detail: { reason: 'video-on-free', message: '...' }
 *   }));
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, Check, ArrowRight, Crown } from "lucide-react";
import { useCurrency } from "@/hooks/useCurrency";
import { PACKAGES, priceFor, formatMoney } from "@/config/pricing";

const HIGHLIGHT_IDS = ["starter", "growth", "pro"];
const planHighlights = (currency) => HIGHLIGHT_IDS.map((id) => {
  const p = PACKAGES.find((x) => x.id === id);
  return { id, name: p.name, price: formatMoney(priceFor(p, currency), currency), popular: !!p.popular,
           desc: `${p.adsPerMonth} finished 15s ads / mo · ${p.creditsPerMonth} credits${p.rollover ? " · 1-month rollover" : ""}` };
});

const REASON_COPY = {
  "video-on-free": {
    title: "Video generation is a paid feature",
    body: "Your free plan includes one watermarked 15s ad. Upgrade to Starter for 3 finished ads a month, or keep making images with your remaining credits.",
  },
  "image-limit": {
    title: "You've used your trial credits",
    body: "Upgrade to keep generating. Starter gives you 3 finished ads a month, or buy a top-up pack from your dashboard.",
  },
  credits: {
    title: "Not enough credits for that",
    body: "Every generation is metered in credits (image 2 · B-roll clip 5 · finished ad 10). Top up from your dashboard — top-up credits never expire — or upgrade for a monthly allowance.",
  },
  "video-limit": {
    title: "Monthly video limit reached",
    body: "You've hit your plan's video cap for this month. Upgrade to a higher tier or grab a credit top-up to keep generating.",
  },
  generic: {
    title: "Upgrade to keep creating",
    body: "Pick a plan that fits your volume. Cancel anytime.",
  },
};

export function UpgradeModal() {
  const { currency } = useCurrency();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("generic");
  const [overrideMessage, setOverrideMessage] = useState("");

  useEffect(() => {
    const handler = (e) => {
      setReason(e.detail?.reason || "generic");
      setOverrideMessage(e.detail?.message || "");
      setOpen(true);
    };
    window.addEventListener("lumora:upgrade-required", handler);
    return () => window.removeEventListener("lumora:upgrade-required", handler);
  }, []);

  if (!open) return null;
  const copy = REASON_COPY[reason] || REASON_COPY.generic;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={() => setOpen(false)}
      data-testid="upgrade-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-2xl w-full glass rounded-3xl p-8 ring-1 ring-white/10 max-h-[90vh] overflow-y-auto"
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          data-testid="upgrade-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{copy.title}</h2>
            <p className="text-white/40 text-xs font-medium uppercase tracking-wider">Upgrade required</p>
          </div>
        </div>
        <p className="text-white/65 text-sm mb-6 leading-relaxed">
          {overrideMessage || copy.body}
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          {planHighlights(currency).map((p) => (
            <Link
              key={p.id}
              to="/#pricing"
              onClick={() => setOpen(false)}
              className={`relative rounded-2xl p-4 transition-all hover:-translate-y-0.5 ${
                p.popular
                  ? "bg-gradient-to-br from-violet-600/15 to-indigo-600/15 ring-2 ring-violet-500/50"
                  : "bg-white/[0.04] ring-1 ring-white/10 hover:ring-white/20"
              }`}
              data-testid={`upgrade-modal-plan-${p.id}`}
            >
              {p.popular && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-violet-500 text-white text-[10px] font-bold uppercase tracking-wider">
                  Best Value
                </span>
              )}
              <p className="text-white text-base font-bold mb-1">{p.name}</p>
              <p className="text-2xl font-extrabold text-white">{p.price}<span className="text-xs text-white/40 font-medium">/mo</span></p>
              <p className="text-white/55 text-xs mt-2 leading-snug">{p.desc}</p>
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 text-white/40 text-xs mb-5">
          <Check className="w-3.5 h-3.5 text-violet-400" />
          <span>Top-up credits never expire</span>
          <span className="text-white/20">·</span>
          <Check className="w-3.5 h-3.5 text-violet-400" />
          <span>Cancel any time</span>
        </div>

        <div className="flex gap-3">
          <Link
            to="/#pricing"
            onClick={() => setOpen(false)}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold text-sm transition-all"
            data-testid="upgrade-modal-cta"
          >
            <Sparkles className="w-4 h-4" /> See All Plans <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="px-5 py-3 rounded-full text-white/55 hover:text-white hover:bg-white/5 text-sm font-medium transition-colors"
            data-testid="upgrade-modal-dismiss"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
